// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * Single source of truth for converting `pets.dob` between its stored form
 * (Unix SECONDS) and the "YYYY-MM-DD" strings that date inputs speak.
 *
 * There are TWO writers of this field and they do not share a code path:
 *   - PetEdit's <DateInput>, which goes through the react-admin dataProvider;
 *   - RegisterOwner, which POSTs /api/users/register-owner directly, bypassing
 *     the dataProvider (and therefore its dob handling) entirely.
 * The bound below lives here rather than at either call site because bounding
 * only one of them is how this field got corrupted in the first place.
 *
 * Reading: the form normally does NOT see raw seconds —
 * dataProvider.convertPetDobToDateString rewrites dob to "YYYY-MM-DD" on every
 * read path, getOne included. The number branch is a fallback for a record that
 * reached a form without that conversion.
 *
 * `format` must be IDEMPOTENT: format(format(x)) === format(x). react-admin
 * applies it twice — useInput formats field.value, then DateInput re-formats
 * that already-formatted string into the input's defaultValue
 * (ra-ui-materialui DateInput.tsx, `defaultValue={format(...)}`). A converter
 * that only accepts seconds returns '' on the second pass and blanks the field
 * for EVERY pet, whichever shape the record arrived in — worse than the bug it
 * was meant to fix, because a vet seeing an empty DOB may retype a guess.
 *
 * Six production pets carry year-like dobs (2016, 2014, 2013, 2013, 2010, 2010)
 * from an earlier round trip that stored a parseFloat of an ISO string; ada-core
 * now rejects that with a 400 (Pets.dob beforeValidate).
 */

/**
 * Plausibility bounds. <input type="date"> accepts a SIX-digit year, so one
 * extra keystroke in the year segment turns 2020 into 152020 — observed on beta
 * storing dob 4735126022400 with nothing rejecting it. ada-core's beforeValidate
 * enforces only a floor (100000), and the value then reads back as '+152020-03',
 * because toISOString() switches to expanded-year notation past year 9999 and
 * slice(0, 10) cuts it mid-field.
 *
 * DOB_MIN matches ada-core's floor rather than something more generous: below it
 * the write 400s anyway, so accepting it here would only turn a silent bad value
 * into a failed save. A date of birth cannot be in the future, so the ceiling is
 * now plus a day of clock skew.
 */
export const DOB_MIN_SECONDS = 100_000
export const DOB_FUTURE_SKEW_SECONDS = 86_400

export const isPlausibleDobSeconds = (secs: number): boolean =>
  Number.isFinite(secs) &&
  secs >= DOB_MIN_SECONDS &&
  secs <= Math.floor(Date.now() / 1000) + DOB_FUTURE_SKEW_SECONDS

export const dobSecondsToDateInput = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  // Already formatted upstream by the dataProvider — pass it through rather
  // than pushing it through Number(), which yields NaN and blanks the field.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.slice(0, 10)))
    return value.slice(0, 10)
  const secs = Number(value)
  if (!Number.isFinite(secs) || secs <= 0) return ''
  const d = new Date(secs * 1000)
  if (Number.isNaN(d.getTime())) return ''
  const iso = d.toISOString()
  // Expanded-year output ('+152020-03-03T...') means the stored value is out of
  // range; render nothing rather than a truncated string the input can't show.
  return iso.startsWith('+') || iso.startsWith('-') ? '' : iso.slice(0, 10)
}

/**
 * Returns seconds for a usable date, null for a deliberate clear, and undefined
 * for anything implausible.
 *
 * undefined rather than null is load-bearing on the EDIT path. dataProvider
 * .update() writes `dob: null` for null — erasing the stored date — and omits
 * the key entirely for undefined. AutoSave fires on a 2s debounce while the vet
 * is still typing, so mapping an out-of-range date to null would silently
 * destroy a good dob. On the CREATE path (RegisterOwner) there is nothing to
 * destroy, so both null and undefined simply mean "send no dob".
 */
export const dateInputToDobSeconds = (value: unknown): number | null | undefined => {
  if (value === null || value === undefined || value === '') return null
  const ms = new Date(value as string).getTime()
  if (Number.isNaN(ms)) return undefined
  const secs = Math.floor(ms / 1000)
  return isPlausibleDobSeconds(secs) ? secs : undefined
}

/**
 * Render a stored dob as the CALENDAR DAY it represents, in the viewer's locale
 * format.
 *
 * dob stores an instant but means a day: the write path stores UTC midnight and
 * the edit form reads it back with toISOString(), so the UTC calendar date is
 * the intended one. Rendering the instant with a bare
 * `new Date(secs * 1000).toLocaleDateString()` converts it to the VIEWER's zone,
 * which shows the PREVIOUS day for anything stored before 07:00Z — 321 of 893
 * production pets when viewed from US Pacific, i.e. a wrong age on a clinical
 * record, and a list that disagreed with the edit form for the same pet.
 *
 * So: take the UTC year/month/day, rebuild them as a LOCAL date, and let the
 * locale format it. The day is then invariant under the viewer's timezone.
 */
export const formatDobForDisplay = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''

  // The LIST views never see seconds: convertPetDobToDateString rewrites dob to
  // "YYYY-MM-DD" on every read path (getList included), so a string is the
  // NORMAL input here and the number branch is the fallback. Getting this
  // backwards is how the DoB column broke — the previous code did
  // `new Date(dob * 1000)` on the string and rendered "Invalid Date" for every
  // pet; a seconds-only replacement renders an empty cell instead. Same trap as
  // the edit-form converters (L-028): check what the dataProvider hands you.
  if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
    if (!m) return ''
    const day = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    return Number.isNaN(day.getTime()) ? '' : day.toLocaleDateString()
  }

  const secs = Number(value)
  if (!Number.isFinite(secs) || secs <= 0) return ''
  const instant = new Date(secs * 1000)
  if (Number.isNaN(instant.getTime())) return ''
  const asCalendarDay = new Date(
    instant.getUTCFullYear(),
    instant.getUTCMonth(),
    instant.getUTCDate(),
  )
  return Number.isNaN(asCalendarDay.getTime()) ? '' : asCalendarDay.toLocaleDateString()
}
